import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';

import { useCommonAppServices } from '@provider-portal/common/services/ContextProviders';
import { EventProps } from '@provider-portal/common/services/MixpanelService';
import { PerformanceTier } from '@provider-portal/common/api/openapi_gen/deliveryProviderPortal/deliveryProviderPortal/models';
import {
  BoltStarMonthlyUpdateDialog,
  BoltStarMonthlyUpdateHeader,
} from '@provider-portal/design-common/clay/components';
import {
  acknowledgeBoltStarMonthlyUpdate,
  BoltStarMonthlyUpdate,
  BOLT_STAR_ROUTE,
  BoltStarAnalyticsEvents,
  getMonthName,
  getNextMonth,
  PopupClosingType,
  TIER_META,
  useTrackBoltStarEvent,
} from '@provider-portal/mp2-common/topPerformer';
import silverPercentFlowerIcon from '@provider-portal/mp2-common/topPerformer/assets/icon-percent-flower-silver.png';

import { $tk } from 'utils/appTranslationKey';

const TIER_LABEL_KEYS: Record<PerformanceTier, string> = {
  poor: $tk.widgets.boltStar.tier.needsImprovement,
  needs_improvement: $tk.widgets.boltStar.tier.needsImprovement,
  good: $tk.widgets.boltStar.tier.good,
  top: $tk.widgets.boltStar.tier.top,
};

interface BoltStarMonthlyUpdateDialogContainerProps {
  update: BoltStarMonthlyUpdate;
  providerId: number;
}

export function BoltStarMonthlyUpdateDialogContainer({
  update,
  providerId,
}: BoltStarMonthlyUpdateDialogContainerProps) {
  const intl = useIntl();
  const history = useHistory();
  const { apiClient } = useCommonAppServices();
  const trackBoltStarEvent = useTrackBoltStarEvent();

  const { tier, month } = update;
  const [isOpen, setIsOpen] = useState(true);

  const trackEvent = useCallback(
    (event: BoltStarAnalyticsEvents, props: EventProps = {}) =>
      trackBoltStarEvent(event, {
        tier,
        evaluation_month: `${month.year}-${String(month.month).padStart(2, '0')}`,
        ...props,
      }),
    [trackBoltStarEvent, tier, month],
  );

  const hasTrackedOpen = useRef(false);
  useEffect(() => {
    if (!isOpen || hasTrackedOpen.current) {
      return;
    }

    hasTrackedOpen.current = true;
    trackEvent(BoltStarAnalyticsEvents.MONTHLY_UPDATE_POPUP_SHOWN);
  }, [isOpen, trackEvent]);

  const dismiss = useCallback(() => {
    setIsOpen(false);
    void acknowledgeBoltStarMonthlyUpdate({ apiClient, providerId, month });
  }, [apiClient, providerId, month]);

  const handleClose = useCallback(() => {
    trackEvent(BoltStarAnalyticsEvents.MONTHLY_UPDATE_POPUP_CLOSED, {
      closing_type: PopupClosingType.CLOSE_BUTTON,
    });
    dismiss();
  }, [dismiss, trackEvent]);

  const handleAction = useCallback(() => {
    trackEvent(BoltStarAnalyticsEvents.MONTHLY_UPDATE_POPUP_CLOSED, {
      closing_type: PopupClosingType.CALL_TO_ACTION,
    });
    dismiss();
    history.push(BOLT_STAR_ROUTE);
  }, [dismiss, history, trackEvent]);

  const tierLabel = intl.formatMessage({ id: TIER_LABEL_KEYS[tier] });
  const { leftIcon, rightIcon } = TIER_META[tier];

  const header: BoltStarMonthlyUpdateHeader =
    tier === PerformanceTier.TOP
      ? {
          kind: 'illustration',
          alt: intl.formatMessage({ id: $tk.widgets.boltStarMonthlyUpdateDialog.illustrationAlt }),
        }
      : {
          kind: 'level',
          caption: intl.formatMessage(
            { id: $tk.widgets.boltStarMonthlyUpdateDialog.levelCaption },
            { month: getMonthName(month) },
          ),
          tierLabel,
          wings: leftIcon && rightIcon ? { leftSrc: leftIcon, rightSrc: rightIcon } : undefined,
          benefit:
            tier === PerformanceTier.GOOD
              ? {
                  iconSrc: silverPercentFlowerIcon,
                  label: intl.formatMessage({
                    id: $tk.widgets.boltStarMonthlyUpdateDialog.benefit.smartPromotions,
                  }),
                }
              : undefined,
        };

  const copy = {
    [PerformanceTier.NEEDS_IMPROVEMENT]: {
      title: $tk.widgets.boltStarMonthlyUpdateDialog.needsImprovement.title,
      description: $tk.widgets.boltStarMonthlyUpdateDialog.needsImprovement.description,
      actionLabel: $tk.widgets.boltStarMonthlyUpdateDialog.actionLabel.seeGoals,
    },
    [PerformanceTier.GOOD]: {
      title: $tk.widgets.boltStarMonthlyUpdateDialog.good.title,
      description: $tk.widgets.boltStarMonthlyUpdateDialog.good.description,
      actionLabel: $tk.widgets.boltStarMonthlyUpdateDialog.actionLabel.seeGoals,
    },
    [PerformanceTier.TOP]: {
      title: $tk.widgets.boltStarMonthlyUpdateDialog.top.title,
      description: $tk.widgets.boltStarMonthlyUpdateDialog.top.description,
      actionLabel: $tk.widgets.boltStarMonthlyUpdateDialog.actionLabel.checkItOut,
    },
  }[tier];

  return (
    <BoltStarMonthlyUpdateDialog
      isOpen={isOpen}
      onClose={handleClose}
      onAction={handleAction}
      header={header}
      title={intl.formatMessage({ id: copy.title })}
      description={intl.formatMessage(
        { id: copy.description },
        { nextMonth: getMonthName(getNextMonth(month)) },
      )}
      actionLabel={intl.formatMessage({ id: copy.actionLabel })}
      closeLabel={intl.formatMessage({ id: $tk.widgets.boltStarMonthlyUpdateDialog.closeLabel })}
    />
  );
}
